import {
  AuditOutlined,
  ApiOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  FileZipOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Empty, Input, message, Modal, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ArtifactTestPortfolioRow, CURRENT_TEST_GENERATOR, testDemoApi } from './testDemoApi';

const { Option } = Select;

function resultCounts(row: ArtifactTestPortfolioRow) {
  const results = row.latestRun?.results ?? [];
  return {
    passed: results.filter(result => result.status === 'passed').length,
    failed: results.filter(result => result.status === 'failed').length,
    total: results.length,
  };
}

export const TestCenterPage: React.FC = () => {
  const history = useHistory();
  const [rows, setRows] = useState<ArtifactTestPortfolioRow[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [environment, setEnvironment] = useState('all');
  const [version, setVersion] = useState('all');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReleasePlanId, setSelectedReleasePlanId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await testDemoApi.listPortfolio();
      setRows(result.rows);
      setError('');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Test portfolio could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const available = rows.filter(row => row.artifact && (!row.testPlan || row.testPlan.generator !== CURRENT_TEST_GENERATOR));
  const versions = Array.from(new Set(rows.flatMap(row => row.artifact?.version ? [row.artifact.version] : [])));
  const visible = useMemo(() => rows.filter(row => {
    const counts = resultCounts(row);
    const rowStatus = !row.testPlan || row.testPlan.generator !== CURRENT_TEST_GENERATOR ? 'not-generated' : !row.latestRun ? 'not-run' : counts.failed ? 'failed' : row.latestRun.status;
    const term = search.toLowerCase();
    return (row.releasePlan.targetAppName.toLowerCase().includes(term) || (row.artifact?.fileName.toLowerCase().includes(term) ?? false))
      && (status === 'all' || status === rowStatus)
      && (environment === 'all' || environment === row.releasePlan.environment)
      && (version === 'all' || version === row.artifact?.version);
  }), [environment, rows, search, status, version]);

  const testedRows = rows.filter(row => row.latestRun?.report);
  const averageCoverage = testedRows.length
    ? Math.round(testedRows.reduce((sum, row) => sum + (row.latestRun?.report?.confidence ?? 0), 0) / testedRows.length)
    : 0;
  const releaseReady = rows.filter(row => row.latestRun?.status === 'passed').length;
  const needsAttention = rows.filter(row => row.latestRun?.status === 'failed').length;

  const openGenerate = () => {
    setSelectedReleasePlanId(available[0]?.releasePlan.id ?? '');
    setModalOpen(true);
  };

  const generate = async () => {
    const row = available.find(item => item.releasePlan.id === selectedReleasePlanId);
    if (!row?.artifact) return;
    setGenerating(true);
    try {
      const plan = await testDemoApi.generatePlan(row.releasePlan.id, row.artifact.id);
      message.success(`Generated ${plan.cases.length} artifact-aware checks.`);
      setModalOpen(false);
      history.push(`/tests/plans/${plan.id}/execution`);
    } catch (generateError) {
      message.error(generateError instanceof Error ? generateError.message : 'Test plan could not be generated.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="test-center-page">
      <div className="page-heading test-center-heading">
        <div className="test-title-group"><span className="test-title-icon"><ExperimentOutlined /></span><div><span className="test-eyebrow">QUALITY INTELLIGENCE</span><h1>Qlik App Validation</h1><p>Generate App-aware test suites, execute deterministic Qlik checks, and explain release confidence.</p></div></div>
        <Button type="primary" icon={<AuditOutlined />} onClick={openGenerate}>Generate App-aware plan</Button>
      </div>

      <section className="test-overview-strip" aria-label="Test portfolio summary">
        <div><AuditOutlined /><span><strong>{rows.filter(row => row.testPlan).length}</strong><small>Test plans</small></span></div>
        <div className="is-success"><CheckCircleOutlined /><span><strong>{releaseReady}</strong><small>Release ready</small></span></div>
        <div className="is-danger"><WarningOutlined /><span><strong>{needsAttention}</strong><small>Needs attention</small></span></div>
        <div className="is-confidence"><SafetyCertificateOutlined /><span><strong>{averageCoverage}%</strong><small>Average confidence</small></span></div>
      </section>

      <section className="ai-test-blueprint" aria-label="Autonomous Qlik validation workflow">
        <div className="ai-test-blueprint-intro">
          <span className="ai-badge"><RobotOutlined /> AI TEST COMPOSER</span>
          <strong>One artifact becomes a complete, evidence-bound test suite</strong>
          <p>AI chooses coverage from the App itself; deterministic Qlik execution decides whether the artifact may be released.</p>
        </div>
        <div className="ai-test-blueprint-steps">
          <div><b>1</b><span className="blueprint-icon"><FileZipOutlined /></span><strong>Inspect artifact</strong><small>Exact QVF, Git SHA and checksum</small></div>
          <div><b>2</b><span className="blueprint-icon is-ai"><RobotOutlined /></span><strong>Compose test matrix</strong><small>Objects, fields, expressions and visuals</small></div>
          <div><b>3</b><span className="blueprint-icon"><ApiOutlined /></span><strong>Execute in Qlik</strong><small>Disposable App, reload and Engine checks</small></div>
          <div><b>4</b><span className="blueprint-icon is-evidence"><SafetyCertificateOutlined /></span><strong>Explain &amp; gate</strong><small>AI narrative backed by signed evidence</small></div>
        </div>
        <div className="ai-test-evidence-contract"><DatabaseOutlined /><span><strong>Evidence contract</strong><small>AI can propose and interpret. Qlik Engine, Cloud API, QVF checksum and round-trip assertions remain authoritative.</small></span></div>
      </section>

      <div className="test-toolbar">
        <Input aria-label="Search test plans" prefix={<SearchOutlined />} placeholder="Search target App or artifact..." value={search} onChange={event => setSearch(event.target.value)} />
        <Select aria-label="Test status" value={status} onChange={setStatus}><Option value="all">Test status</Option><Option value="passed">Passed</Option><Option value="failed">Needs attention</Option><Option value="not-run">Not run</Option><Option value="not-generated">Plan not generated</Option></Select>
        <Select aria-label="Test environment" value={environment} onChange={setEnvironment}><Option value="all">Environment</Option><Option value="Production">Production</Option><Option value="Staging">Staging</Option><Option value="QA">QA</Option></Select>
        <Select aria-label="Artifact version" value={version} onChange={setVersion}><Option value="all">Artifact version</Option>{versions.map(item => <Option key={item} value={item}>{item}</Option>)}</Select>
        <Button aria-label="Refresh test portfolio" icon={<ReloadOutlined />} loading={loading} onClick={() => void load()} />
      </div>

      {error && <Alert type="error" showIcon message="Quality Center is unavailable" description={error} action={<Button size="small" onClick={() => void load()}>Retry</Button>} />}
      {loading && !rows.length ? <div className="test-loading"><Spin /><span>Reading artifacts and validation evidence…</span></div> : (
        <div className="test-portfolio-table" role="table" aria-label="Qlik artifact test plans">
          <div className="test-portfolio-head" role="row"><span>Target App / Artifact</span><span>AI-generated coverage</span><span>Deterministic checks</span><span>Release confidence</span><span>Last validation</span><span>Next step</span></div>
          {visible.map(row => {
            const counts = resultCounts(row);
            const report = row.latestRun?.report;
            return <div className="test-portfolio-row" role="row" key={row.releasePlan.id}>
              <div className="test-target-cell"><span className="app-glyph"><BarChartOutlined /></span><div><strong>{row.releasePlan.targetAppName}</strong><span>{row.artifact ? `${row.artifact.version} · ${row.artifact.fileName}` : 'No release artifact'}</span></div></div>
              <div className="test-coverage-cell">{row.testPlan?.generator === CURRENT_TEST_GENERATOR ? <><strong>{row.testPlan.cases.length} content-derived tests</strong><span>{row.testPlan.inventory.appObjects} objects · {row.testPlan.inventory.measures} measures</span><span>{row.testPlan.inventory.visualObjects} visuals · {row.testPlan.inventory.dimensions} dimensions</span></> : <span className="test-empty-value">App-content plan required</span>}</div>
              <div className={`test-check-state ${counts.failed ? 'is-failed' : counts.total ? 'is-passed' : ''}`}>{counts.total ? <><span><CheckCircleOutlined /> {counts.passed}/{counts.total} Passed</span>{counts.failed > 0 && <span><WarningOutlined /> {counts.failed} Failed</span>}</> : <span>—</span>}</div>
              <div className={`test-confidence ${report?.risk ? `is-${report.risk.toLowerCase()}` : ''}`}>{report ? <><strong>{report.risk === 'Low' ? 'High' : report.risk === 'Medium' ? 'Medium' : 'Low'}</strong><span>{report.confidence}%</span></> : <span>—</span>}</div>
              <div className="test-last-run">{row.latestRun ? <><span>{new Date(row.latestRun.createdAt).toLocaleString()}</span><small>{row.latestRun.id}</small></> : <span>—</span>}</div>
              <div className="row-actions">{row.testPlan?.generator === CURRENT_TEST_GENERATOR ? <Link className="test-next-link" to={`/tests/plans/${row.testPlan.id}/${row.latestRun ? 'reports' : 'execution'}`}>{row.latestRun?.status === 'failed' ? 'Investigate' : row.latestRun ? 'Open report' : 'Run validation'} ›</Link> : row.artifact ? <Button type="link" onClick={() => { setSelectedReleasePlanId(row.releasePlan.id); setModalOpen(true); }}>Generate App plan ›</Button> : <span>Artifact required</span>}</div>
            </div>;
          })}
          {!visible.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No artifact test plans match the current filters." />}
        </div>
      )}

      <p className="test-evidence-note"><ExperimentOutlined /> AI proposes coverage and explains results. Qlik API, reload, artifact, and round-trip assertions remain the evidence source.</p>

      <Modal title="Generate App-aware test plan" open={modalOpen} okText="Inspect App & generate" confirmLoading={generating} okButtonProps={{ disabled: !selectedReleasePlanId }} onOk={() => void generate()} onCancel={() => setModalOpen(false)}>
        <Alert type="info" showIcon message="The generator reads the approved App content" description="Click-CI checks out the artifact's exact Git revision, inventories real Qlik objects, measures, dimensions and visualizations, then creates named tests bound to those targets. Execution re-exports the isolated Qlik App and compares live evidence." />
        <div className="test-generation-contract">
          <div><RobotOutlined /><span><strong>AI-generated coverage</strong><small>Names business-readable cases and selects relevant targets from the artifact inventory.</small></span></div>
          <div><ApiOutlined /><span><strong>Scripted verification</strong><small>Reload, Engine selection, expression, checksum and QVF round-trip assertions produce the proof.</small></span></div>
        </div>
        <label className="test-generate-field"><span>Target App and sealed artifact</span><Select aria-label="Artifact to test" value={selectedReleasePlanId || undefined} placeholder="Select an untested artifact" onChange={setSelectedReleasePlanId}>{available.map(row => <Option key={row.releasePlan.id} value={row.releasePlan.id}><FileZipOutlined /> {row.releasePlan.targetAppName} · {row.artifact?.version} · {row.artifact?.sha256.slice(0, 12)}</Option>)}</Select></label>
        {!available.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Every available artifact already has a test plan." />}
      </Modal>
    </section>
  );
};
